import { Controller } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from 'lucide-react';

type DateFieldProps = {
  name: string;
  rules?: unknown;
  placeholder?: string;
};

/** This component depend on a react-hook-form context provider */
export function DateField({ name, rules, placeholder = 'Pick a date' }: DateFieldProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        // Convert field value to Date object for the calendar
        const dateValue = field.value ? new Date(field.value) : undefined;

        return (
          <div className="space-y-1">
            <Popover>
              <PopoverTrigger asChild>
                <div
                  data-empty={!field.value}
                  role="button"
                  tabIndex={0}
                  className="data-[empty=true]:text-muted-foreground border text-base h-10 rounded-xl px-3 py-2 md:text-sm cursor-pointer flex items-center flex-row-reverse justify-between gap-3">
                  <CalendarIcon className="h-3 w-3" />
                  {field.value ? (
                    Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long',
                    }).format(new Date(field.value))
                  ) : (
                    <span>{placeholder}</span>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dateValue}
                  onSelect={(date) => {
                    if (date) {
                      // Store as timestamp
                      field.onChange(date.getTime());
                    }
                  }}
                  disabled={(date) => {
                    // Disable past dates
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                  autoFocus
                />
              </PopoverContent>
            </Popover>
            {fieldState.error && (
              <p className="text-sm font-medium text-destructive mt-1">{fieldState.error.message}</p>
            )}
          </div>
        );
      }}
    />
  );
}
